import { useState } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ScrollToTop from '@/components/feature/ScrollToTop';

export default function AboutPage() {
  const [formStatus, setFormStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [formMessage, setFormMessage] = useState('');

  const handleContactSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setFormStatus('loading');

    const form = e.currentTarget;
    const formData = new FormData(form);
    const honeypot = formData.get('company_alt') as string;
    if (honeypot && honeypot.trim() !== '') {
      setFormStatus('success');
      setFormMessage('感谢您的留言！我们会尽快回复。');
      form.reset();
      return;
    }

    try {
      const params = new URLSearchParams();
      formData.forEach((value, key) => {
        if (key !== 'company_alt' && value.toString().trim()) {
          params.append(key, value.toString());
        }
      });
      const response = await fetch('https://readdy.ai/api/form/d9u58en8l97n3eas3hl0', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: params.toString(),
      });
      const responseText = await response.text();
      let parsed: any = {};
      try { parsed = JSON.parse(responseText); } catch {}
      if (response.ok && parsed?.code === 'OK') {
        setFormStatus('success');
        setFormMessage('感谢您的留言！我们会尽快回复。');
        form.reset();
      } else {
        setFormStatus('error');
        setFormMessage(parsed?.meta?.message || parsed?.message || '提交失败，请稍后再试。');
      }
    } catch (_err) {
      setFormStatus('error');
      setFormMessage('网络错误，请稍后再试。');
    }
  };

  const breadcrumbItems = [
    { label: '首页', href: '/' },
    { label: '关于本站' },
  ];

  const pageJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'AboutPage',
    name: '关于佐山爱粉丝站',
    description: '了解佐山爱粉丝站的品牌介绍、信誉声明和联系方式。',
    url: 'https://chengtuitui.cn/about',
    inLanguage: 'zh-CN',
  };

  const faqJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: [
      {
        '@type': 'Question',
        name: '佐山爱粉丝站是什么？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '佐山爱粉丝站是专注于日本女演员及写真偶像佐山爱的中文信息聚合平台，为粉丝提供个人资料、作品全览及最新动态。',
        },
      },
      {
        '@type': 'Question',
        name: '本站的内容来源是什么？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '本网站内容来源于公开渠道信息和官方发布资料，由编辑团队整理、翻译和撰写，确保信息的准确性和时效性。',
        },
      },
      {
        '@type': 'Question',
        name: '如何联系本站？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '您可以通过本页面的联系表单或发送邮件至contact@chengtuitui.cn与我们取得联系。',
        },
      },
      {
        '@type': 'Question',
        name: '本站是否与佐山爱本人有直接关联？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '佐山爱粉丝站是由粉丝自发建立的非官方信息平台，与佐山爱本人及其所属经纪公司无直接隶属关系。我们致力于提供真实、全面的信息，但部分内容可能存在时效性差异。',
        },
      },
    ],
  };

  return (
    <>
      <ScrollToTop />
      <script type="application/ld+json">{JSON.stringify(pageJsonLd)}</script>
      <script type="application/ld+json">{JSON.stringify(faqJsonLd)}</script>
      <Navbar />
      <main>
        <div className="pt-20 bg-background-50">
          <div className="w-full px-4 md:px-6 max-w-4xl mx-auto">
            <Breadcrumb items={breadcrumbItems} />
            <div className="mt-6 mb-6">
              <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900">
                关于本站
              </h1>
              <p className="text-sm text-foreground-500 mt-2">
                了解佐山爱粉丝站的品牌故事与使命
              </p>
            </div>
          </div>
        </div>

        <section className="py-10 md:py-12 bg-background-50">
          <div className="w-full px-4 md:px-6 max-w-4xl mx-auto">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 md:gap-10 mb-12">
              <div>
                <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-primary-50 text-primary-500 mb-4">
                  <i className="ri-heart-pulse-line text-2xl"></i>
                </div>
                <h2 className="font-heading text-lg md:text-xl font-semibold text-foreground-900 mb-3">
                  品牌介绍
                </h2>
                <p className="text-sm text-foreground-600 leading-relaxed mb-3">
                  佐山爱粉丝站创立于2024年，是中文互联网上最全面的佐山爱信息聚合平台。
                  我们致力于为华语地区的佐山爱粉丝提供最准确、最及时、最全面的资讯服务。
                </p>
                <p className="text-sm text-foreground-600 leading-relaxed mb-3">
                  从个人资料到写真作品，从影视作品到最新动态，我们的编辑团队精心整理每一篇内容，
                  确保粉丝能够在第一时间获取感兴趣的信息。
                </p>
                <p className="text-sm text-foreground-600 leading-relaxed">
                  作为一个由粉丝自发建立的平台，我们秉持着对佐山爱的热爱和对粉丝负责的态度，
                  持续为社区提供高品质内容。
                </p>
              </div>

              <div className="relative w-full h-64 md:h-72 rounded-xl overflow-hidden">
                <img
                  src="https://readdy.ai/api/search-image?query=Elegant%20minimalist%20studio%20photography%20warm%20cream%20tones%20soft%20natural%20lighting%20Japanese%20aesthetic%20atmosphere%20artistic%20composition%20gentle%20gradients%20high%20quality%20editorial%20style&width=800&height=600&seq=sayama-about-banner&orientation=landscape"
                  alt="佐山爱粉丝站"
                  className="w-full h-full object-cover object-top"
                />
              </div>
            </div>

            <div className="mb-12">
              <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-accent-50 text-accent-500 mb-4">
                <i className="ri-shield-check-line text-2xl"></i>
              </div>
              <h2 className="font-heading text-lg md:text-xl font-semibold text-foreground-900 mb-3">
                信誉声明
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                <div className="p-5 rounded-xl bg-background-100 border border-background-200/60">
                  <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-secondary-100 text-secondary-600 mb-3">
                    <i className="ri-information-line text-lg"></i>
                  </div>
                  <h3 className="font-heading text-sm font-semibold text-foreground-900 mb-2">信息准确</h3>
                  <p className="text-xs text-foreground-500 leading-relaxed">
                    所有信息均来源于公开渠道和官方发布，经编辑团队核实后发布。
                  </p>
                </div>
                <div className="p-5 rounded-xl bg-background-100 border border-background-200/60">
                  <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-secondary-100 text-secondary-600 mb-3">
                    <i className="ri-timer-line text-lg"></i>
                  </div>
                  <h3 className="font-heading text-sm font-semibold text-foreground-900 mb-2">更新及时</h3>
                  <p className="text-xs text-foreground-500 leading-relaxed">
                    编辑团队持续跟踪佐山爱最新动态，确保内容时效性。
                  </p>
                </div>
                <div className="p-5 rounded-xl bg-background-100 border border-background-200/60">
                  <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-secondary-100 text-secondary-600 mb-3">
                    <i className="ri-user-voice-line text-lg"></i>
                  </div>
                  <h3 className="font-heading text-sm font-semibold text-foreground-900 mb-2">粉丝为本</h3>
                  <p className="text-xs text-foreground-500 leading-relaxed">
                    所有内容以粉丝需求为导向，提供最优质的阅读体验。
                  </p>
                </div>
              </div>
            </div>

            <div>
              <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-secondary-50 text-secondary-500 mb-4">
                <i className="ri-mail-line text-2xl"></i>
              </div>
              <h2 className="font-heading text-lg md:text-xl font-semibold text-foreground-900 mb-3">
                联系我们
              </h2>
              <p className="text-sm text-foreground-500 mb-6">
                如果您有任何问题、建议或合作意向，欢迎通过以下表单与我们联系。我们会尽快回复。
              </p>

              <div className="grid grid-cols-1 lg:grid-cols-5 gap-8">
                <div className="lg:col-span-3">
                  <form onSubmit={handleContactSubmit} className="space-y-4" data-readdy-form="">
                    <div className="honeypot-wrapper">
                      <input type="text" name="company_alt" tabIndex={-1} autoComplete="off" aria-hidden="true" readOnly />
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <label htmlFor="contact-name" className="block text-sm font-label text-foreground-700 mb-1.5">
                          您的姓名 <span className="text-primary-500">*</span>
                        </label>
                        <input
                          id="contact-name"
                          type="text"
                          name="name"
                          required
                          className="w-full px-4 py-2.5 rounded-lg bg-background-100 border border-background-200/70 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-300 focus:ring-1 focus:ring-primary-200 font-label"
                          placeholder="请输入您的姓名"
                        />
                      </div>
                      <div>
                        <label htmlFor="contact-email" className="block text-sm font-label text-foreground-700 mb-1.5">
                          电子邮箱 <span className="text-primary-500">*</span>
                        </label>
                        <input
                          id="contact-email"
                          type="email"
                          name="email"
                          required
                          className="w-full px-4 py-2.5 rounded-lg bg-background-100 border border-background-200/70 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-300 focus:ring-1 focus:ring-primary-200 font-label"
                          placeholder="请输入您的邮箱"
                        />
                      </div>
                    </div>
                    <div>
                      <label htmlFor="contact-subject" className="block text-sm font-label text-foreground-700 mb-1.5">
                        主题 <span className="text-primary-500">*</span>
                      </label>
                      <input
                        id="contact-subject"
                        type="text"
                        name="subject"
                        required
                        className="w-full px-4 py-2.5 rounded-lg bg-background-100 border border-background-200/70 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-300 focus:ring-1 focus:ring-primary-200 font-label"
                        placeholder="请输入留言主题"
                      />
                    </div>
                    <div>
                      <label htmlFor="contact-message" className="block text-sm font-label text-foreground-700 mb-1.5">
                        留言内容 <span className="text-primary-500">*</span>
                      </label>
                      <textarea
                        id="contact-message"
                        name="message"
                        required
                        maxLength={500}
                        rows={5}
                        className="w-full px-4 py-2.5 rounded-lg bg-background-100 border border-background-200/70 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-300 focus:ring-1 focus:ring-primary-200 resize-none font-label"
                        placeholder="请输入您的留言内容（最多500字）"
                      ></textarea>
                      <p className="text-xs text-foreground-400 mt-1">最多500个字符</p>
                    </div>
                    <button
                      type="submit"
                      disabled={formStatus === 'loading'}
                      className="whitespace-nowrap cursor-pointer px-6 py-3 bg-primary-500 text-background-50 rounded-lg text-sm font-label hover:bg-primary-600 transition-colors disabled:opacity-60"
                    >
                      {formStatus === 'loading' ? (
                        <>
                          <i className="ri-loader-4-line animate-spin mr-2"></i>提交中...
                        </>
                      ) : (
                        <>
                          <i className="ri-send-plane-line mr-2"></i>发送留言
                        </>
                      )}
                    </button>

                    {formMessage && (
                      <p className={`text-sm ${formStatus === 'success' ? 'text-emerald-600' : 'text-primary-600'}`}>
                        {formMessage}
                      </p>
                    )}
                  </form>
                </div>

                <div className="lg:col-span-2 space-y-6">
                  <div className="p-5 rounded-xl bg-background-100 border border-background-200/60">
                    <div className="flex items-start gap-3">
                      <div className="w-9 h-9 flex items-center justify-center flex-shrink-0 rounded-lg bg-accent-50 text-accent-500">
                        <i className="ri-mail-line"></i>
                      </div>
                      <div>
                        <h4 className="font-heading text-sm font-semibold text-foreground-900 mb-1">电子邮箱</h4>
                        <p className="text-sm text-foreground-500">contact@chengtuitui.cn</p>
                      </div>
                    </div>
                  </div>
                  <div className="p-5 rounded-xl bg-background-100 border border-background-200/60">
                    <div className="flex items-start gap-3">
                      <div className="w-9 h-9 flex items-center justify-center flex-shrink-0 rounded-lg bg-accent-50 text-accent-500">
                        <i className="ri-time-line"></i>
                      </div>
                      <div>
                        <h4 className="font-heading text-sm font-semibold text-foreground-900 mb-1">响应时间</h4>
                        <p className="text-sm text-foreground-500">我们通常在24小时内回复您的留言</p>
                      </div>
                    </div>
                  </div>
                  <div className="p-5 rounded-xl bg-background-100 border border-background-200/60">
                    <div className="flex items-start gap-3">
                      <div className="w-9 h-9 flex items-center justify-center flex-shrink-0 rounded-lg bg-accent-50 text-accent-500">
                        <i className="ri-information-line"></i>
                      </div>
                      <div>
                        <h4 className="font-heading text-sm font-semibold text-foreground-900 mb-1">温馨提示</h4>
                        <p className="text-sm text-foreground-500">本站为粉丝自发建立的非官方信息平台，与佐山爱本人及其经纪公司无直接关联。</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="py-12 md:py-14 bg-background-100">
          <div className="w-full px-4 md:px-6 max-w-4xl mx-auto">
            <h2 className="font-heading text-xl md:text-2xl font-semibold text-foreground-900 mb-8 text-center">
              常见问题
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="p-5 rounded-xl bg-background-50 border border-background-200/60">
                <h3 className="font-heading text-sm font-semibold text-foreground-900 mb-2">
                  佐山爱粉丝站是什么？
                </h3>
                <p className="text-sm text-foreground-500 leading-relaxed">
                  佐山爱粉丝站是专注于日本女演员及写真偶像佐山爱的中文信息聚合平台，为粉丝提供个人资料、作品全览及最新动态。
                </p>
              </div>
              <div className="p-5 rounded-xl bg-background-50 border border-background-200/60">
                <h3 className="font-heading text-sm font-semibold text-foreground-900 mb-2">
                  本站的内容来源是什么？
                </h3>
                <p className="text-sm text-foreground-500 leading-relaxed">
                  本网站内容来源于公开渠道信息和官方发布资料，由编辑团队整理、翻译和撰写，确保信息的准确性和时效性。
                </p>
              </div>
              <div className="p-5 rounded-xl bg-background-50 border border-background-200/60">
                <h3 className="font-heading text-sm font-semibold text-foreground-900 mb-2">
                  如何联系本站？
                </h3>
                <p className="text-sm text-foreground-500 leading-relaxed">
                  您可以通过本页面的联系表单或发送邮件至contact@chengtuitui.cn与我们取得联系。
                </p>
              </div>
              <div className="p-5 rounded-xl bg-background-50 border border-background-200/60">
                <h3 className="font-heading text-sm font-semibold text-foreground-900 mb-2">
                  本站是否与佐山爱本人有直接关联？
                </h3>
                <p className="text-sm text-foreground-500 leading-relaxed">
                  佐山爱粉丝站是由粉丝自发建立的非官方信息平台，与佐山爱本人及其所属经纪公司无直接隶属关系。
                </p>
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}